import React, { useEffect } from 'react';
import { FaShieldAlt, FaUserCheck, FaMapMarkerAlt, FaPhoneAlt, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const Safety = () => {
  useEffect(() => {
    document.title = "Safety Guidelines - SastaMastaRide";
  }, []);

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <FaShieldAlt className="text-blue-600 text-5xl mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-2">Safety Guidelines</h1>
          <p className="text-gray-600">Your safety is our top priority. Follow these guidelines for a safe ride experience.</p>
        </div>

        {/* For Passengers */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaUserCheck className="mr-2 text-green-600" />
            Safety Tips for Passengers
          </h2>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <FaCheckCircle className="mr-2 text-green-600" />
                Before Booking
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Check rider's profile, ratings, and reviews before booking</li>
                <li>Verify that the rider is verified (look for verification badge)</li>
                <li>Read trip details carefully including vehicle type and route</li>
                <li>Share your trip details with a trusted contact</li>
              </ul>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <FaCheckCircle className="mr-2 text-blue-600" />
                During the Trip
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Always wear a helmet (mandatory for two-wheelers)</li>
                <li>Share your live location with a trusted contact</li>
                <li>Verify the vehicle and rider match the booking details</li>
                <li>Keep your phone charged and accessible</li>
                <li>Trust your instincts - if something feels wrong, don't proceed</li>
                <li>Don't share personal information unnecessarily</li>
              </ul>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <FaExclamationTriangle className="mr-2 text-yellow-600" />
                Emergency Situations
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Use the emergency contact feature in the app</li>
                <li>Call local emergency services (100/112) if needed</li>
                <li>Report any safety concerns immediately to our support team</li>
                <li>Take photos/videos if safe to do so for evidence</li>
              </ul>
            </div>
          </div>
        </section>

        {/* For Riders */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaShieldAlt className="mr-2 text-blue-600" />
            Safety Tips for Riders
          </h2>
          <div className="space-y-4">
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <FaCheckCircle className="mr-2 text-green-600" />
                Vehicle Safety
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Ensure your vehicle is in good condition and regularly serviced</li>
                <li>Check brakes, tires, lights, and fuel before each trip</li>
                <li>Carry valid vehicle registration and insurance documents</li>
                <li>Provide helmets for all passengers (mandatory)</li>
                <li>Follow traffic rules and speed limits</li>
              </ul>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <FaCheckCircle className="mr-2 text-blue-600" />
                Passenger Safety
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Verify passenger identity matches booking details</li>
                <li>Ensure all passengers wear helmets</li>
                <li>Don't exceed vehicle capacity</li>
                <li>Drive defensively and avoid risky maneuvers</li>
                <li>Be respectful and maintain professional behavior</li>
              </ul>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
              <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                <FaCheckCircle className="mr-2 text-purple-600" />
                Personal Safety
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-1">
                <li>Keep your phone charged and accessible</li>
                <li>Share trip details with family/friends</li>
                <li>Trust your instincts - cancel if you feel unsafe</li>
                <li>Don't share personal information unnecessarily</li>
                <li>Report any suspicious behavior to support</li>
              </ul>
            </div>
          </div>
        </section>

        {/* General Safety Rules */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">General Safety Rules</h2>
          <div className="bg-gray-50 p-6 rounded-lg">
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li><strong>No Alcohol/Drugs:</strong> Never drive or ride under the influence of alcohol or drugs</li>
              <li><strong>Helmet Mandatory:</strong> All passengers must wear helmets on two-wheelers</li>
              <li><strong>Valid Documents:</strong> Riders must have valid driving license and vehicle documents</li>
              <li><strong>Age Restrictions:</strong> Passengers must be at least 18 years old</li>
              <li><strong>No Harassment:</strong> Zero tolerance for harassment, discrimination, or inappropriate behavior</li>
              <li><strong>Respect Privacy:</strong> Don't ask for personal information or contact details</li>
              <li><strong>Follow Route:</strong> Stick to the agreed route unless passenger requests changes</li>
            </ul>
          </div>
        </section>

        {/* Reporting Issues */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
            <FaPhoneAlt className="mr-2 text-red-600" />
            Reporting Safety Issues
          </h2>
          <div className="bg-red-50 p-6 rounded-lg border-l-4 border-red-500">
            <p className="text-gray-700 mb-4">
              If you experience or witness any safety issues, report them immediately:
            </p>
            <div className="space-y-2 text-gray-700">
              <p><strong>In-App Reporting:</strong> Use the "Report Issue" button in the trip details</p>
              <p><strong>Email:</strong> safety@sastamastaride.com</p>
              <p><strong>Phone:</strong> +91-1800-XXX-XXXX (24/7 Safety Hotline)</p>
              <p><strong>Emergency:</strong> Call 100/112 for immediate police assistance</p>
            </div>
            <p className="text-gray-700 mt-4">
              All reports are taken seriously and investigated promptly. We take strict action against 
              violations including account suspension or permanent ban.
            </p>
          </div>
        </section>

        {/* Verification Process */}
        <section>
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Rider Verification Process</h2>
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-gray-700 mb-4">
              All riders on SastaMastaRide go through a comprehensive verification process:
            </p>
            <ol className="list-decimal list-inside text-gray-700 space-y-2">
              <li><strong>Identity Verification:</strong> Valid government ID proof</li>
              <li><strong>License Verification:</strong> Valid driving license check</li>
              <li><strong>Vehicle Verification:</strong> Vehicle registration and insurance documents</li>
              <li><strong>Background Check:</strong> Basic background verification</li>
              <li><strong>Profile Review:</strong> Profile and vehicle photos verification</li>
            </ol>
            <p className="text-gray-700 mt-4">
              Only verified riders can offer rides on our platform, ensuring your safety and security.
            </p>
          </div>
        </section>

        {/* Contact Info */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-lg text-center">
          <FaShieldAlt className="text-4xl mx-auto mb-3" />
          <h3 className="text-xl font-semibold mb-2">Safety is Our Priority</h3>
          <p className="mb-4">
            We're committed to providing a safe ridesharing experience. If you have any safety concerns 
            or suggestions, please contact us.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/help" className="bg-white text-blue-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition">
              Contact Support
            </a>
            <a href="mailto:safety@sastamastaride.com" className="bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-600 transition">
              Email Safety Team
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Safety;

